"use client";

import { motion, useInView } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { Mail, Shield, Zap, Users } from "lucide-react";

function AnimatedCounter({ target, suffix = "" }: { target: number; suffix?: string }) {
    const [count, setCount] = useState(0);
    const ref = useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, { once: true });

    useEffect(() => {
        if (!isInView) return;
        let start = 0;
        const duration = 2000;
        const step = target / (duration / 16);
        const timer = setInterval(() => {
            start += step;
            if (start >= target) {
                setCount(target);
                clearInterval(timer);
            } else {
                setCount(Math.floor(start));
            }
        }, 16);
        return () => clearInterval(timer);
    }, [isInView, target]);

    return (
        <div ref={ref} className="text-3xl sm:text-4xl font-black tracking-tight text-gray-900 dark:text-white">
            {count.toLocaleString()}{suffix}
        </div>
    );
}

const stats = [
    { icon: Mail, value: 10000, suffix: "+", label: "Emails Delivered", color: "from-indigo-500 to-blue-500" },
    { icon: Shield, value: 99, suffix: "%", label: "Deliverability Rate", color: "from-emerald-500 to-teal-500" },
    { icon: Zap, value: 100, suffix: "%", label: "Real-Time Tracking", color: "from-amber-500 to-orange-500" },
    { icon: Users, value: 500, suffix: "+", label: "Active Organizations", color: "from-purple-500 to-pink-500" },
];

export default function StatsBar() {
    const ref = useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    return (
        <section ref={ref} className="relative py-16 px-6">
            <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                    {stats.map((stat, i) => (
                        <motion.div
                            key={stat.label}
                            initial={{ opacity: 0, y: 30 }}
                            animate={isInView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.6, delay: i * 0.1 }}
                            className="relative group p-6 rounded-3xl bg-white/60 dark:bg-white/5 backdrop-blur-xl border border-gray-100 dark:border-white/10 shadow-sm hover:shadow-xl transition-all duration-500"
                        >
                            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center mb-4 shadow-lg`}>
                                <stat.icon className="h-5 w-5 text-white" />
                            </div>
                            <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mt-1">{stat.label}</p>

                            {/* Hover glow */}
                            <div className={`absolute inset-0 rounded-3xl bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-[0.03] transition-opacity duration-500`} />
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
